<template>

  <div class="dmb-show" :class="orientationClass">
    <div class="dmb-slides-holder">
      <transition-group
          :name="slideTransition"
          :duration="transitionDuration"
          @after-enter="onAfterEnter"
          tag="div">

        <dmb-slide
            v-for="slideItem in visibleSlides"
            :key="slideItem.id"
            v-show="slide?.id === slideItem.id"
            :orientation="$props.orientation"
            :slide="slideItem"
            :style="getSlideStyle(slideItem)">

          <dmb-widget
              v-for="widget in slideItem.widgets"
              :key="widget.id"
              :widget="widget"
              :slide-duration="slideItem.duration * 1000"
              :slide-pending="slideItem.id === slide?.id"
              :slide-visible="slideItem.id === visibleSlideId"
          />
        </dmb-slide>

      </transition-group>
    </div>
  </div>

</template>

<script>

import {Options, Vue} from 'vue-class-component';
import {first, sortBy} from 'lodash';

import transitionTypeClassFilter from '../filter/transitionTypeClass';

import Slide from './Slide.vue';
import Widget from './widget/Widget.vue';
import SlideConditionType from '../enum/SlideConditionType';
import PromotionState from '../enum/PromotionState';
import {PromotionStoreMutation} from '../store/promotions';

export default Options({
  name: 'dmb-show',
  props: {
    show: Object,
    slideId: String,
    orientation: String,
    hardStart: Boolean,
    once: Boolean,
    // Optional sync mode: when both are provided, slides advance off the
    // server-time anchor instead of a chained setTimeout. Two screens in the
    // same System receive the same syncAnchorAt and compute the same slide
    // index from their measured serverNow() — transitions stay aligned
    // within ~50-100ms. When either is null, the original local-timer flow
    // runs unchanged, which preserves editor previews (once: true) and any
    // screen whose System isn't in sync mode.
    syncAnchorAt: { type: Number, default: null },
    serverNow: { type: Function, default: null }
  },
  components: {
    'dmb-slide': Slide,
    'dmb-widget': Widget
  }
})(
    class Show extends Vue {

      slide = null;
      transitionDuration = null;
      slideDuration = null;
      visibleSlideId = null;
      transitionTimeout = null;

      cachedSlides = null;
      cacheKey = null;
      slideTransitionCache = null;

      get orientationClass() {
        return {
          portrait: this.orientation === 'PORTRAIT',
          landscape: this.orientation === 'LANDSCAPE'
        }
      }

      getSlideStyle(slideItem) {
        return {
          'z-index': slideItem.id === this.slide?.id ? 200 : 100,
          'will-change': slideItem.id === this.slide?.id ? 'transform, opacity' : 'auto'
        };
      }

      mounted() {
        this.start();
        this.$watch('show', this.handleShowChange, {immediate: false});
        // Re-anchor without remounting when the server stamps a new anchor
        // (collection switch, campaign activate, sync just turned on).
        this.$watch('syncAnchorAt', () => {
          if (this.isSyncMode()) {
            this.startSync();
          }
        }, {immediate: false});
      }

      unmounted() {
        this.cleanup();
      }

      handleShowChange() {
        this.cachedSlides = null;
        this.cacheKey = null;
        this.slideTransitionCache = null;

        this.start();
      }

      cleanup() {
        if (this.transitionTimeout) {
          window.clearTimeout(this.transitionTimeout);
          this.transitionTimeout = null;
        }
      }

      isSyncMode() {
        return this.syncAnchorAt != null && typeof this.serverNow === 'function' && !this.once;
      }

      start(arm = false) {
        // Sync mode takes a separate code path that computes the slide index
        // deterministically from the anchor. Editor previews (once: true)
        // and screens whose System isn't in sync mode fall through to the
        // original setTimeout-chain logic below — keeping all the existing
        // hardStart / slide-0 promotion-randomizer / single-slide behavior
        // exactly as it was.
        if (this.isSyncMode()) {
          this.startSync();
          return;
        }

        const firstSlide = this.slideId ?
            this.slides.find(slide => slide.id === this.slideId) :
            first(this.slides);

        if (firstSlide) {
          this.showSlide(firstSlide, true, arm);
        } else {
          this.cancelTransition();
          this.slide = null;
          this.visibleSlideId = null;
        }
      }

      startSync() {
        this.cancelTransition();

        const slides = this.slides;
        if (!slides || slides.length === 0) {
          this.slide = null;
          this.visibleSlideId = null;
          return;
        }

        const position = this.computeSyncPosition();
        if (!position) {
          // Fallback: anchor invalid or all durations 0 — behave like the
          // local-timer path so the screen isn't stuck on a blank.
          this.showSlide(first(slides), true, false);
          return;
        }

        const slide = position.slide;
        const transitionDuration = slide.transitionDuration || 0.5;

        this.$style.setVar('dmb-animation-duration', `${transitionDuration}s`);
        this.transitionDuration = slide.transitionType ? transitionDuration * 1000 : null;
        this.slideDuration = slide.duration * 1000;
        this.visibleSlideId = null;
        this.slide = slide;

        if (slides.length === 1) {
          this.$nextTick(() => {
            this.visibleSlideId = slide.id;
          });
          // No transition to schedule — single-slide loop.
          return;
        }

        // Schedule the next recompute at the moment this slide should end.
        // The callback re-runs computeSyncPosition() against the anchor,
        // so any drift in this setTimeout doesn't accumulate — it self-
        // corrects on every transition.
        this.transitionTimeout = window.setTimeout(
            () => this.startSync(),
            Math.max(50, position.remainingMs)
        );
      }

      computeSyncPosition() {
        const slides = this.slides;
        if (!slides || slides.length === 0) return null;

        const durations = slides.map(s => Math.max(0, (s.duration || 0) * 1000));
        const totalMs = durations.reduce((a, b) => a + b, 0);
        if (totalMs <= 0) return null;

        const elapsed = ((this.serverNow() - this.syncAnchorAt) % totalMs + totalMs) % totalMs;

        let accum = 0;
        for (let i = 0; i < slides.length; i++) {
          const next = accum + durations[i];
          if (elapsed < next) {
            return {
              slide: slides[i],
              index: i,
              remainingMs: next - elapsed
            };
          }
          accum = next;
        }

        // Shouldn't reach here given the modulo, but fall back safely.
        return { slide: slides[slides.length - 1], index: slides.length - 1, remainingMs: 0 };
      }

      showSlide(slide, firstRun = false, arm = false) {
        this.cancelTransition();

        const slideIndex = this.slides.indexOf(slide);
        const nextSlideIndex = (slideIndex + 1) % this.slides.length;
        const nextSlide = this.slides[nextSlideIndex];

        if (slideIndex === 0 && !firstRun && this.slides.length > 1) {
          this.$emit('finish');

          if (this.once) {
            return;
          }
        }

        if (this.hardStart && slideIndex === 0) {

          this.$style.setVar('dmb-animation-duration', `0s`);
          this.transitionDuration = null;
        } else {

          const transitionDuration = slide.transitionDuration || 0.5;
          this.$style.setVar('dmb-animation-duration', `${transitionDuration}s`);

          if (slide.transitionType) {
            this.transitionDuration = transitionDuration * 1000;
          } else {
            this.transitionDuration = null;
          }
        }

        if (slideIndex === 0) {
          this.$store.commit(PromotionStoreMutation.UPDATE_RANDOM_PROMOTION);
        }

        this.slideDuration = slide.duration * 1000;
        this.visibleSlideId = null;
        this.slide = slide;

        if (this.slides.length === 1 && !firstRun) {
          this.$nextTick(() => {
            this.visibleSlideId = slide.id;
          });
        }

        if (!arm) {
          this.transitionTimeout = window.setTimeout(() => this.showSlide(nextSlide), this.slideDuration);
        }
      }

      onAfterEnter() {
        this.visibleSlideId = this.slide?.id;
      }

      cancelTransition() {
        if (this.transitionTimeout) {
          window.clearTimeout(this.transitionTimeout);
          this.transitionTimeout = null;
        }
      }

      get slides() {
        const promotions = this.$store.state.promotions.promotions;
        const promotionKeys = promotions.map(p => p.id + '_' + p.state).join('_');
        const cacheKey = (this.show?.id || 'none') + '_' + promotions.length + '_' + promotionKeys;

        if (this.cacheKey === cacheKey && this.cachedSlides) {
          return this.cachedSlides;
        }

        const filtered = this.show?.slides?.filter(slide => {
          if (slide.conditionType === SlideConditionType.PROMOTION_ACTIVE_ANY) {
            return promotions.length > 0;
          } else if (slide.conditionType === SlideConditionType.PROMOTION_ACTIVE_SPECIFIC && slide.conditionValue) {
            const promotion = this.$store.getters.getPromotion(slide.conditionValue);
            return promotion && promotion.state === PromotionState.AVAILABLE;
          }
          return true;
        }) || [];

        this.cachedSlides = sortBy(filtered, 'sortIndex');
        this.cacheKey = cacheKey;

        return this.cachedSlides;
      }

      get visibleSlides() {
        if (!this.slide) return [];

        const currentIndex = this.slides.findIndex(s => s.id === this.slide.id);
        if (currentIndex === -1) return [this.slide];

        const nextIndex = (currentIndex + 1) % this.slides.length;
        return [this.slides[currentIndex], this.slides[nextIndex]];
      }

      get slideTransition() {
        const type = this.slide?.transitionType;

        if (this.slideTransitionCache && this.slideTransitionCache.type === type) {
          return this.slideTransitionCache.value;
        }

        const value = type ? transitionTypeClassFilter(type) : null;
        this.slideTransitionCache = {type, value};
        return value;
      }
    }
);

</script>
<style src="../../scss/component/Show.scss" lang="scss"></style>